import {
  FC,
  HTMLAttributes,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Spinner } from "../Spinner";
import { reverseWobbly } from "utils/constants";
import { SpinDirection, StageSize } from "types";
import { TextStyle } from "pixi.js";
import { getAvatar } from "utils/get-avatar";
import { ApngPlayer } from "components/ApngPlayer";
import {
  animated,
  useSpring,
  easings,
  config,
} from "@react-spring/web";
import { usePlayer, useCurrency } from "hooks";
import { getRandomRange } from "utils/number";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import ExplosionImg from "assets/images/apng/explosion.png";
import FireworksImg from "assets/images/apng/fireworks.png";
import { useDebounceCallback, useInterval, useTimeout } from "usehooks-ts";
import { useOrientation } from "hooks/orientation";

const Avatar = styled(animated.div)<{ $image: string, $bottom: number }>`
  position: absolute;
  width: 78px;
  height: 75%;
  margin: 0 auto;
  bottom: ${props => props.$bottom + 21}px;

  &:after {
    content: '';
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    background: url(${props => props.$image}) center bottom / contain no-repeat;
  }
`;

const Chips = styled.div<{ $height: number; $y: number, $scale: number }>`
  position: absolute;
  width: 105px;
  height: ${(props) => props.$height}px;
  background: url(images/chips.png) 0 ${(props) => props.$y}px / 100% auto no-repeat;
  bottom: 0;
  transform: scale(${props => props.$scale});
  transform-origin: center bottom;
`;

const TextBox = styled(animated.div)`
  width: 100%;
  height: 26px;
  margin: 0 auto 3px auto;
  position: relative;
  border-radius: 13px;
  background-color: #0f0;
  overflow: hidden;
  transform: scale(0);
  z-index: 1;

  & > canvas {
    transform-origin: top left;
    transform: scale(0.5);
  }

  @media (orientation: portrait) {
    height: 30px;
    margin-bottom: 5px;
    border-radius: 15px;
  }
`;

const Fold = styled(TextBox)`
  background-color: rgba(0, 0, 0, 0.7);
  box-sizing: border-box;
  border: 1px solid #f00;
  font-size: 18px;
  color: #f00;
  text-align: center;
  transform: scale(1);
`;

const Text = styled.div<{ $bb: number, $bottom: number }>`
  position: relative;
  font-size: 1.25rem;
  font-weight: normal;
  line-height: 0.9;
  letter-spacing: normal;
  text-align: center;
  color: ${(props) => props.$bb < 25 ? "#ff1800" : props.$bb < 60 ? "#4593ff" : "#00ff00"};
  margin-bottom: ${props => props.$bottom}px;

  @media (orientation: portrait) {
    font-size: 1.75rem;
    line-height: 1;
  }
`;

const Container = styled(animated.div)<{ $scale: number }>`
  position: relative;
  width: 100%;
  height: ${props => props.$scale * 93}%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

const AllIn = styled(animated.div)`
  width: 60px;
  height: 54px;
  left: calc((100% - 58px) / 2);
  position: relative;
  margin: 0 auto 2px 0;
  background: url(images/all-in.png) center / contain no-repeat;
  
  @media (orientation: portrait) {
    width: 50px;
    height: 45px;
    left: calc((100% - 50px) / 2);
    margin-bottom: 3px;
  }
`;

const Glow = styled(animated.div)<{ $bottom: number }>`
  width: 160px;
  height: 183px;
  position: absolute;
  left: calc((100% - 160px) / 2);
  bottom: ${props => props.$bottom + 10}px;
  background-image: radial-gradient(circle at 50%, #bea261, #856d36 21%, rgba(0, 0, 0, 0) 64%);
  
  @media (orientation: portrait) {
    bottom: ${props => props.$bottom + 25}px;
  }
`;

export type StackProps = HTMLAttributes<HTMLDivElement> & {
  id: string;
  bounty: number;
  isAllIn: boolean;
  hideState: number;
  skip: boolean;
  topPlayer: boolean;
  isBust: boolean;
  newBb: number;
  isFold: boolean;
  scale: number;
  onFinish: () => void;
};

const Fireworks = () => {
  const pos = [
    { x: -100, y: 50 },
    { x: -175, y: 20 },
    { x: -75, y: 10 },
    { x: -200, y: 35 },
  ];

  return (
    <>
      {pos.map((value, i) =>
        <ApngPlayer 
          key={i}
          src={FireworksImg}
          delay={0.5 + 0.75 * i}
          rate={1.5 + i * 0.5}
          hideOnEnd={true}
          style={{
            position: "absolute",
            width: "292px",
            height: "292px",
            left: `${value.x}%`,
            top: `${value.y}%`,
            filter: `sepia(100%) saturate(3000%) hue-rotate(${Math.floor(Math.random() * 360)}deg) brightness(90%)`,
          }}
        />
      )}
    </>
  );
};

export const Stack: FC<StackProps> = ({
  id,
  bounty,
  isAllIn,
  hideState,
  skip,
  topPlayer,
  isBust,
  newBb,
  isFold,
  scale,
  onFinish,
}) => {
  const { t } = useTranslation();
  const [currentBb, setCurrentBb] = useState(0);
  const [stageSize, setStageSize] = useState<StageSize>({
    width: 0,
    height: 0,
  });
  const [spinSeconds, setSpinSeconds] = useState(-1);
  const textBoxRef = useRef<HTMLDivElement>(null);
  const { index, time } = usePlayer(id);
  const finishRef = useRef(onFinish);
  const bountyRef = useRef(-1);
  const { parseCurrency } = useCurrency();
  const startBbRef = useRef(-1);
  const play = useSoundStore(store => store.play);
  const fadeOut = useSoundStore(store => store.fadeOut);
  const getHowl = useSoundStore(store => store.getHowl);
  const { portrait } = useOrientation();
  const textStyle = useRef(new TextStyle({
    align: "center",
    fontFamily: "Teko",
    fontSize: portrait ? 40 : 36,
    fontWeight: "700",
    fill: "rgba(0, 0, 0, 0.75)",
  }));
  const transY = `translateY(${portrait && !index ? -2 : 0}px)`;
  const transform = `scale(0) ${transY}`;

  useEffect(() => {
    setStageSize({
      width: (textBoxRef.current?.clientWidth || 0) * 2,
      height: (textBoxRef.current?.clientHeight || 0) * 2,
    });
  }, []);

  useTimeout(() => {
    setSpinSeconds(skip ? 0 : bountyRef.current < 0 ? 4 : 2);
  }, !!index || bounty === bountyRef.current ? null : bountyRef.current < 0 && !skip ? 2000 : 0);

  const floorChips = (bb: number) => {
    return Math.floor(bb / 5);
  };

  const getY = (bb: number) => {
    let y = 35;
    let n = bb;
    while (n > 0) {
      y += 35 + n * 10;
      n--;
    }
    return y - 1955;
  };

  const getMaxChips = (bb: number) => {
    if(newBb > 80 && !startBbRef.current){
      return Math.floor(bb * 16 / newBb);
    }else{
      return Math.min(floorChips(bb), 16);
    }
  };

  const [spring0, api0] = useSpring(() => ({
    transform,
    opacity: 1,
    config: key => {
      return key === 'transform' ? config.wobbly : {};
    }
  }));

  useEffect(() => {
    if(isAllIn){
      api0.start({
        transform: `scale(${portrait && !index ? 1.15 : 1}) ${transY}`,
        delay: 150 * index,
        onStart: () => {
          play(Sounds.CardReveal);
        }
      });
    }else{
      api0.start({
        opacity: 0,
        onRest: () => {
          api0.set({
            transform,
            opacity: 1,
          })
        }
      });
    }
  }, [api0, index, isAllIn, play, portrait, transY, transform]);

  const [spring1, api1] = useSpring(() => ({
    transform: 'scale(0)',
    opacity: 0,
    config: key => {
      return key === 'opacity' ? { duration: 500, easing: easings.easeOutSine } : {};
    }
  }));

  const [flashSpring, flashApi] = useSpring(() => ({
    from: { transform: `scale(${!!index ? 1 : 1.2})`, filter: `brightness(1) invert(0)` }
  }));

  useEffect(() => {
    if(!skip) return;

    api1.stop();
    if(!index && !isBust){
      api1.set({
        opacity: 1,
        transform: 'scale(1)'
      });
    }
  }, [skip, api1, isBust, index]);

  useEffect(() => {
    finishRef.current = onFinish;
  }, [onFinish]);

  const handleSpinFinish = useCallback(() => {
    bountyRef.current = bounty;
    fadeOut(Sounds.ChipUp);
    fadeOut(Sounds.MoveUp);
    finishRef.current();
    setSpinSeconds(-1);
  }, [bounty, fadeOut]);

  const debSpinFinish = useDebounceCallback(useCallback(() => {
    fadeOut(Sounds.Fireworks);
    handleSpinFinish();
  }, [fadeOut, handleSpinFinish]), topPlayer ? 3000 : 500);

  const toggleBb = useCallback(() => {
    if(topPlayer && !isBust){
      play(Sounds.Fireworks);
      api1.start({
        to: [
          { transform: `scale(2)` },
          { transform: `scale(1)` },
          { transform: `scale(1.75)` },
          { transform: `scale(1)` },
        ],
        config: {
          duration: 250,
          easing: easings.easeInOutSine,
        },
        onRest: debSpinFinish
      });
    }else{
      api1.start({
        transform: `scale(${isBust ? 0 : 1})`,
        config: isBust? reverseWobbly : config.wobbly,
        onRest: () => {
          if(!!index || !newBb) handleSpinFinish();
        }
      });
    }
  }, [api1, debSpinFinish, handleSpinFinish, index, isBust, newBb, play, topPlayer]);

  const toggleAvatar = useCallback(() => {
    if(isBust){
      api1.start({ opacity: 0 });
    }else if(!index && bountyRef.current === bounty){
      debSpinFinish();
    }else{
      api1.start({
        opacity: 1,
        onRest: () => {
          if(!index) {
            flashApi.start({
              to: [
                { transform: `scale(1.3)`, filter: `brightness(0) invert(1)`, config: {duration: 150, easing: easings.linear}, onRest: toggleBb },
                { filter: `brightness(1) invert(0)`, config: {duration: 100, easing: easings.linear} },
                { transform: `scale(1.1)`, filter: `brightness(0) invert(1)`, config: {duration: 125, easing: easings.linear} },
                { transform: `scale(1.2)`, filter: `brightness(1) invert(0)`, config: {duration: 125, easing: easings.easeOutSine} },
              ]
            });
          }else{
            toggleBb();
          }
        },
      });
    }
  }, [api1, bounty, debSpinFinish, flashApi, index, isBust, toggleBb]);

  useEffect(() => {
    if(currentBb === newBb) {
      startBbRef.current = -1;
      toggleAvatar();
    }
  }, [currentBb, newBb, toggleAvatar]);

  useEffect(() => {
    if (floorChips(currentBb) === floorChips(newBb)) {
      setCurrentBb(newBb);
    }
  }, [currentBb, newBb]);

  useInterval(() => {
    if(startBbRef.current < 0) startBbRef.current = currentBb;
    const delta = Math.ceil(Math.abs(newBb - startBbRef.current) / (isBust && !index ? 100 : 25));
    if (floorChips(currentBb) < floorChips(newBb)) 
      setCurrentBb(Math.round(10 * Math.min(currentBb + delta, newBb)) / 10);
    else 
      setCurrentBb(Math.round(10 * Math.max(currentBb - delta, newBb)) / 10);
  }, currentBb === newBb || (skip && !isBust) ? null : 20);

  useEffect(() => {
    if(isBust){
      play(Sounds.Explode);

      api0.start({
        opacity: 0,
        config: {
          duration: 500,
          easing: easings.easeOutSine
        }
      });

      toggleAvatar();
      toggleBb();
    }
  }, [api0, isBust, play, toggleAvatar, toggleBb]);

  useTimeout(() => play(Sounds.Fold), isFold ? 500 : null);

  useEffect(() => {
    let sound = -1;
    if(startBbRef.current >= 0) return;
    if(newBb > currentBb){
      sound = !index && time <= 1 ? Sounds.ChipUp : Sounds.MoveUp;
    }else if(newBb < currentBb && !isBust){
      sound = !index && time <= 1 ? Sounds.ChipDown : Sounds.MoveDown;
    }
    if(sound >= 0){
      const howl = getHowl(sound);
      if(!howl.playing()) play(sound);
    }
  }, [currentBb, getHowl, index, isBust, newBb, play, time]);

  const bounties = useMemo(() => {
    if(bountyRef.current === bounty || !!index) return [];
    return getRandomRange(Math.max(10, bountyRef.current), bounty);
  }, [bounty, index]); 

  const [contSpring, contApi] = useSpring(() => ({
    opacity: 1,
  }));

  useEffect(() => {
    contApi.start({ 
      opacity: !hideState ? 1 : 0,
      delay: hideState === 3 ? 150 * (index - 1) : hideState === 4 ? 500 : 0,
      config: {
        easing: easings.easeOutSine,
        duration: hideState === 3 ? 500 : hideState === 4 ? 250 : 1000,
      },
    });
  }, [contApi, hideState, index]);

  const spinDirection = useMemo(() => {
    return bountyRef.current < 0 || !bounty ? SpinDirection.Down : SpinDirection.Up;
  }, [bounty]);

  const height = getMaxChips(currentBb) * 10 + (!currentBb ? 0 : 35);
  const ratio = (portrait ? 68 : 60) / 105;

  return ( 
    <Container $scale={portrait ? ratio : 1} style={contSpring}>
      {!index && <Glow style={{ opacity: spring1.opacity }} $bottom={height * ratio} />}
      <Avatar style={{ ...flashSpring, opacity: spring1.opacity }} $image={getAvatar(parseInt(id.slice(-1)))} $bottom={height * ratio + (portrait ? 27 : !index ? 20 * scale - 5 : 0)} data-testid={`player-${index}-avatar`}>
        {topPlayer && !skip && <Fireworks />}
      </Avatar>
      {isFold ? <Fold>{t('FOLD')}</Fold> : <AllIn style={spring0} />}
      <TextBox ref={textBoxRef} style={{ transform: spring1.transform }} data-testid={`player-${index}-bounty`}>
        <Spinner
          {...stageSize}
          textStyle={textStyle.current}
          options={bounties.map((bounty) => parseCurrency(bounty))}
          spinTo={parseCurrency(bounty)}
          spinSeconds={spinSeconds}
          onSpinFinish={handleSpinFinish}
          spinDirection={spinDirection}
          skip={skip}
        />
      </TextBox>
      <Text $bb={currentBb} style={{ opacity: isBust ? 0 : 1 }} $bottom={height * ratio} data-testid={`player-${index}-bb`}>
        {currentBb} BB
      </Text>
      <Chips
        $height={height}
        $y={getY(getMaxChips(currentBb))}
        $scale={ratio}
        style={{ opacity: !currentBb ? 0 : 1 }}
      />
      {isBust && (
        <ApngPlayer
          src={ExplosionImg}
          rate={2.5}
          hideOnEnd={true}
          style={{
            position: "absolute",
            left: "-135px",
            bottom: "-70px",
            width: "400px",
            height: "400px",
            zIndex: 1,
          }}
        />
      )}
    </Container>
  );
};
